import { Divider, Stack } from "@mui/material";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { useUser } from "@/auth/useUser";
import InfoCard from "@/components/common/infoCard/infoCard";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useClientInvoicesQuery } from "@/graphql/queries/client/clientInvoices.graphql.types";
import useUserHasRoles from "@/hooks/user/useUserHasRoles";
import { InvoiceStatus, MEDICAL_DIRECTOR } from "@/types";
import NoPermissionText from "../../common/noPermissionText";
import EmptySection from "../emptySection";
import ExpandableSection from "../expandableSection";
import InvoiceTile from "./invoiceTile";

type Props = {
  clientId: string;
  expanded?: boolean;
};

export default function ClientInvoices({ clientId, expanded }: Props) {
  const { getTokenContext, clientAccessToken } = useClientAccessToken();
  const { hasFeaturePermission, newPermissionsEnabledForUser } = useUser();

  const { data } = useClientInvoicesQuery({
    variables: {
      clientId,
      statusToExclude: clientAccessToken ? InvoiceStatus.DRAFT : "",
    },
    skip: !clientId,
    ...getTokenContext(),
  });
  const invoices = data?.clientByPk?.invoices || [];
  const isMdOrGfe = useUserHasRoles([MEDICAL_DIRECTOR]);
  const canViewClientWallet = newPermissionsEnabledForUser
    ? hasFeaturePermission(FeaturePermission.VIEW_CLIENT_WALLET)
    : !isMdOrGfe;

  return (
    <InfoCard>
      <ExpandableSection
        title="Invoices"
        expanded={expanded || !canViewClientWallet}
      >
        {!canViewClientWallet ? (
          <NoPermissionText />
        ) : (
          <Stack
            divider={<Divider />}
            sx={{
              "& .MuiButtonBase-root:last-child": {
                paddingBottom: 0,
              },
            }}
          >
            {invoices.length > 0 ? (
              invoices.map((invoice) => (
                <InvoiceTile key={invoice.id} invoice={invoice} />
              ))
            ) : (
              <EmptySection>No invoices yet</EmptySection>
            )}
          </Stack>
        )}
      </ExpandableSection>
    </InfoCard>
  );
}
